/**
 * @file ElementPanel.tsx
 * @description Панель выбранного элемента секции (phase19.16 Р2;
 * uiux-design-r2 §4): шапка «← Секция «…» › Кнопка [⋯]» (Дублировать ·
 * Выше · Ниже · Удалить), поля вида (реестр `ELEMENT_PANELS`) и группа
 * «Расположение». Esc — на уровень выше (к секции), кроме полей ввода;
 * при открытии фокус — на шапке панели. Элемента нет (удалили, отменили) —
 * сразу назад к секции.
 *
 * Р2а (uiux-design-r2a §3, §6): у обязательного элемента шаблона товара —
 * полоса `RequiredElementLock`, «Удалить» недоступно, устройства не
 * снимаются; «⋯ Перенести в другой блок ›» — у любого элемента.
 *
 * Приёмка Р2а §7.17 п. 3 (uiux-fonts-usage §4): под полями вида — подсказка
 * о шрифте `ElementFontNote` (части и роли — по текущей форме элемента),
 * одна на все панели реестра, перед «Расположением».
 *
 * SB120 этап 3а (uiux-sb120-stage3 §2): у видов с `typo` — «Цвет и шрифт»
 * по частям (`ElementStyleGroup` → `ElementTypeFields`, контраст — к фону
 * колонки, подложки или секции); отступы — числом px с долей телефона.
 *
 * SB122 (uiux-settings-groups §4.3): три группы-карточки
 * `SettingsGroups panel="element"` — «Содержимое» (поля вида из реестра),
 * «Цвет и шрифт» / «Шрифт» (`ElementStyleGroup`; у элемента без текста
 * группы нет), «Расположение» (свёрнута по умолчанию). Полоса
 * `RequiredElementLock` — над первой группой. Оглавления нет (меньше 4).
 * Ревью партии SB122: группа с замечанием по полям элемента — ✕ в
 * заголовке (реестр `ELEMENT_ISSUE_RULES`); неудачное «Сохранить блок»
 * раскрывает первую такую и даёт фокус полю (`RevealIssueGroup`).
 * SB120 этап 3б: предупреждения об элементе по полям «Содержимого» (в ряд,
 * затемнение плиток, смена объявлений, надписи на фото) — «!» у группы.
 */

import {
  SECTION_LOOK_LIMITS,
  SITE_THEME_LIMITS,
  isProductRequiredKind,
  spaceStepPx,
  type SectionElement,
  type SectionElementKind,
  type SectionPayload,
} from "@chaiberi/shared";
import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useRef, type ComponentType } from "react";

import { Button } from "../../ui/button";
import { useNotify } from "../../ui/NotifyProvider";
import { COLUMN_FULL_TEXT } from "./content/AddElementMenu";
import { isEditableTarget } from "../../../hooks/site-builder/useUndoStack";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { elementMenuActions } from "./content/element-actions";
import { useSectionForm } from "./section-form-context";
import { SettingsGroup, SettingsGroups } from "../../ui/settings-groups";
import { ElementStyleGroup } from "./elements/ElementStyleGroup";
import { ElementPlacementGroup, PLACEMENT_GROUP_ID } from "./elements/ElementPlacementGroup";
import { RequiredElementLock } from "./elements/RequiredElementLock";
import { RevealIssueGroup } from "./RevealIssueGroup";
import { useSectionIssueGroups } from "../../../hooks/site-builder/useSectionIssueGroups";
import { ELEMENT_ISSUE_RULES, groupsWithIssues, type ElementIssueGroup } from "../../../lib/site-builder/issue-groups";
import { ELEMENT_PANELS } from "./elements/registry";
import type { ElementPanelProps } from "./elements/types";
import { duplicateElement, findElement, moveElementBy, removeElement, replaceElement } from "../../../lib/site-builder/section-edit";
import { ELEMENT_KIND_META } from "../../../lib/site-builder/section-meta";
import { elementBg } from "../../../lib/site-builder/text-style";
import { blockWidthPx } from "../../../lib/site-builder/look-size";
import type { OtherH1 } from "../../../lib/site-builder/page-h1";

/** Ключ панели групп: память `commora.settings-groups.element` (общая для всех видов). */
export const ELEMENT_PANEL = "element";
/** «Расположение» нужно редко — свёрнуто по умолчанию (uiux-settings-groups §1). */
const ELEMENT_COLLAPSED = [PLACEMENT_GROUP_ID] as const;
/** Группы панели в порядке на экране (для первой группы с ошибкой). */
const ELEMENT_GROUP_ORDER: readonly ElementIssueGroup[] = ["content", "style", "place"];

interface Props {
  payload: SectionPayload;
  elementId: string;
  sectionTitle: string;
  onChange: (payload: SectionPayload) => void;
  onBack: () => void;
  /** Выбрать другой элемент (копия). */
  onSelect: (elementId: string) => void;
  fresh: boolean;
  onFreshDone: () => void;
  otherH1: OtherH1 | null;
  /** §16.1: счётчики сохранённого блока и признак изменённой формы. */
  dataCounts?: Record<string, number>;
  countsStale?: boolean;
}

export function ElementPanel({ payload, elementId, sectionTitle, onChange, onBack, onSelect, fresh, onFreshDone, otherH1, dataCounts, countsStale = false }: Props) {
  const notify = useNotify();
  const { moveElement, theme, elementWarnings } = useSectionForm();
  const headingRef = useRef<HTMLParagraphElement | null>(null);
  const at = findElement(payload, elementId);
  const missing = at === null;
  const path = at ? `columns.items.${at.col}.elements.${at.row}` : "";
  const bad = useSectionIssueGroups(ELEMENT_ISSUE_RULES, path);
  // SB120 этап 3б: предупреждения по полям «Содержимого» (7–8 в ряд, затемнение, смена) — «!» у группы.
  const warned = useMemo(() => groupsWithIssues(elementWarnings.map((w) => w.path), ELEMENT_ISSUE_RULES, path), [elementWarnings, path]);
  useEffect(() => {
    if (missing) onBack();
  }, [missing, onBack]);
  useEffect(() => {
    headingRef.current?.focus();
  }, [elementId]);
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape" || event.defaultPrevented || isEditableTarget(event.target)) return;
      if (document.querySelector("[data-slot=dialog-content], [data-slot=alert-dialog-content]")) return;
      onBack();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack]);

  if (!at) return null;
  const { element, col, row } = at;
  const meta = ELEMENT_KIND_META[element.kind];
  const column = payload.columns.items[col]?.elements ?? [];
  // Панель своего вида: реестр гарантирует, что вид и панель совпадают.
  const Panel = ELEMENT_PANELS[element.kind] as unknown as ComponentType<ElementPanelProps<SectionElementKind>>;
  const update = (next: SectionElement) => onChange(replaceElement(payload, elementId, next));
  const required = isProductRequiredKind(element.kind);
  // Ритм колонки «как у сайта» — M × плотность (как поле «Между элементами»); отступ элемента добавляется к нему.
  const density = theme?.spacing.density ?? SITE_THEME_LIMITS.density.default;
  const spacing = {
    rhythm: payload.columns.elementGap ?? spaceStepPx("m", density),
    density,
    phoneFactor: theme?.spacing.phoneFactor ?? SECTION_LOOK_LIMITS.phoneFactor.default,
  };
  const actions = {
    duplicate: () => {
      const next = duplicateElement(payload, elementId);
      if (!next) {
        notify.error(COLUMN_FULL_TEXT);
        return;
      }
      onChange(next.payload);
      onSelect(next.id);
    },
    up: () => onChange(moveElementBy(payload, elementId, -1)),
    down: () => onChange(moveElementBy(payload, elementId, 1)),
    remove: () => {
      onChange(removeElement(payload, elementId));
      onBack();
    },
  };

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex min-w-0 items-center gap-1">
        <Button variant="ghost" size="icon" className="size-12 shrink-0 lg:size-10" aria-label={`Назад к секции «${sectionTitle}»`} onClick={onBack}>
          <ArrowLeft aria-hidden />
        </Button>
        {/* Две строки на узком экране: иначе обрезается имя элемента в конце (QA Р2а). */}
        <p ref={headingRef} tabIndex={-1} className="type-body line-clamp-2 min-w-0 flex-1 break-words text-on-surface-variant outline-none" title={`Секция «${sectionTitle}» › ${meta.label}`}>
          <span>Секция «{sectionTitle}»</span> › <span className="font-medium text-on-surface">{meta.label}</span>
        </p>
        <RowActionsMenu
          title={`Элемент «${meta.label}»`}
          size="compact"
          actions={elementMenuActions(element, { first: row === 0, last: row === column.length - 1 }, actions, moveElement ? () => moveElement(elementId) : null)}
        />
      </div>
      {required ? <RequiredElementLock kind={element.kind} /> : null}
      <SettingsGroups panel={ELEMENT_PANEL} defaultCollapsed={ELEMENT_COLLAPSED}>
        <RevealIssueGroup rules={ELEMENT_ISSUE_RULES} base={path} order={ELEMENT_GROUP_ORDER} />
        <SettingsGroup id="content" title="Содержимое" status={bad.has("content") ? "error" : warned.has("content") ? "warning" : undefined}>
          <Panel element={element} onChange={update} path={path} fresh={fresh} onFreshDone={onFreshDone} otherH1={otherH1} dataCount={dataCounts?.[elementId]} countStale={countsStale} columnWidth={(blockWidthPx(payload.look.container) * (payload.columns.shares[col] ?? 12)) / 12} />
        </SettingsGroup>
        <ElementStyleGroup element={element} onChange={update} bg={elementBg(payload, col)} path={path} backLabel={`${meta.label} (секция «${sectionTitle}»)`} invalid={bad.has("style")} />
        <ElementPlacementGroup element={element} onChange={update} locked={required} invalid={bad.has("place")} path={path} spacing={spacing} />
      </SettingsGroups>
    </div>
  );
}
